/**
 * Блок «Референсы под это платье» в карточке «Наших фото»: поиск по всем фото платья
 * запускается сам (бесплатно), находки собраны в карточки моделей «библиотека + модель»
 * (`dressCards`). Переключатель «по какому фото»: «Все фото» — объединение, фото платья —
 * только его находки со своим score; обложка и порядок пересчитываются. Клик по карточке —
 * папка модели или карточка референса в новой вкладке. Выделенные обложки — «Генерировать N»
 * на `/generate` с этим платьем. Смена режима или библиотеки снимает выделение: обложки другие.
 */
import { useQuery } from "@tanstack/react-query";
import { Check, ImageOff, Shirt, X } from "lucide-react";
import { useMemo, useState } from "react";
import { Link } from "react-router";

import { RetryImg } from "@/components/trends/PinGrid";
import { btn, btnPrimary, Empty, ErrorLine, input, Loading, Panel } from "@/components/trends/ui";
import { ApiFailure } from "@/lib/api";
import { cardHref, dressCards, photoCounts, type RefCard, type RefMode } from "@/lib/dressRefs";
import { fittingPath, MAX_PAIRS } from "@/lib/fitting";
import { libraryKeys, loadLibraries, plural, searchSimilar } from "@/lib/library";
import { num, thumbUrl } from "@/lib/media";
import type { Dress, Photo } from "@/lib/photos";
import { retrySimilar, scoreLabel } from "@/lib/similar";

const PAGE = 120;
const NO_HITS: never[] = [];
const EMPTY: ReadonlySet<number> = new Set();

const photoThumb = (p: Photo): string => thumbUrl(p.thumb_path ?? p.view_path);
const noVectors = (e: unknown): boolean =>
  e instanceof ApiFailure && e.items.some((i) => i.code === "NO_VECTORS");

/** У платья без фото блока нет. */
export function DressReferences({ dress }: { dress: Dress }) {
  if (dress.photos.length === 0) return null;
  return <DressReferencesPanel dress={dress} />;
}

function DressReferencesPanel({ dress }: { dress: Dress }) {
  const [mode, setMode] = useState<RefMode>("all");
  const [libraryId, setLibraryId] = useState<number | undefined>(undefined);
  // Выделение и порция помнят, в каком режиме и библиотеке сделаны: сменился ключ (явно или
  // молча, когда режим откатился на «Все фото») — прежний выбор не действует.
  const [sel, setSel] = useState<{ key: string; ids: ReadonlySet<number>; limit: number }>({
    key: "",
    ids: new Set(),
    limit: PAGE,
  });

  const photoIds = useMemo(() => dress.photos.map((p) => p.id).sort((a, b) => a - b), [dress.photos]);
  const photos = useMemo(() => new Map(dress.photos.map((p) => [p.id, p])), [dress.photos]);

  const libraries = useQuery({ queryKey: libraryKeys.list, queryFn: loadLibraries });
  const found = useQuery({
    queryKey: ["dress-refs", dress.id, libraryId ?? "all", photoIds.join(",")],
    queryFn: () => searchSimilar({ dressId: dress.id, ...(libraryId !== undefined ? { libraryId } : {}) }),
    retry: retrySimilar,
    staleTime: 5 * 60_000,
  });
  // Refine держит keepPreviousData глобально: при смене библиотеки в data лежат находки прежней —
  // это не ответ на текущий запрос, показываем загрузку и карточек из него не строим.
  const hits = found.isPlaceholderData ? NO_HITS : (found.data?.hits ?? NO_HITS);
  const counts = useMemo(() => photoCounts(hits, photoIds), [hits, photoIds]);
  // Фото унесли из папки или по нему в этой библиотеке ничего нет — режима больше нет.
  const gone = mode !== "all" && (!photos.has(mode) || (found.data !== undefined && !found.isPlaceholderData && (counts.byPhoto.get(mode) ?? 0) === 0));
  const current: RefMode = gone ? "all" : mode;
  const cards = useMemo(() => dressCards(hits, current), [hits, current]);
  const selKey = `${current}|${libraryId ?? "all"}`;
  const selected = sel.key === selKey ? sel.ids : EMPTY;
  const limit = sel.key === selKey ? sel.limit : PAGE;
  // Выделение — id обложек; считаем только те, что есть среди карточек (порядок экрана).
  const chosen = useMemo(() => cards.filter((c) => selected.has(c.cover.id)), [cards, selected]);

  const setSelected = (ids: ReadonlySet<number>) => setSel({ key: selKey, ids, limit });
  const setLimit = (n: number) => setSel({ key: selKey, ids: selected, limit: n });
  const pickMode = (next: RefMode) => setMode(next);
  const toggle = (coverId: number) => {
    const next = new Set(selected);
    if (!next.delete(coverId)) next.add(coverId);
    setSelected(next);
  };

  return (
    <Panel
      title={
        <>
          Референсы под это платье{" "}
          {found.data ? <span className="tnum font-normal text-fg-faint">{cards.length}</span> : null}
        </>
      }
      aside={
        <select
          className={`${input} max-w-[280px]`}
          value={libraryId === undefined ? "" : String(libraryId)}
          aria-label="в какой библиотеке искать"
          onChange={(e) => {
            setLibraryId(e.target.value === "" ? undefined : Number(e.target.value));
            setMode("all");
          }}
        >
          <option value="">Все библиотеки</option>
          {(libraries.data?.groups ?? []).map((g) => (
            <optgroup key={g.code} label={g.name}>
              {g.libs.map((l) => (
                <option key={l.id} value={String(l.id)}>
                  {l.name} · {num(l.photos)}
                </option>
              ))}
            </optgroup>
          ))}
        </select>
      }
      annot="референсы под платье"
    >
      {found.isPending || found.isPlaceholderData ? (
        <Loading text="Ищу референсы по фото платья…" />
      ) : found.isError ? (
        noVectors(found.error) ? (
          <Empty>У платья нет фото с превью — искать не по чему</Empty>
        ) : (
          <div className="grid justify-items-start gap-2 p-3">
            <ErrorLine error={found.error} />
            <button type="button" className={btn} onClick={() => void found.refetch()} disabled={found.isFetching}>
              Повторить
            </button>
          </div>
        )
      ) : (
        <div className="grid gap-3 p-3">
          <div className="flex flex-wrap items-center gap-2" data-annot="по какому фото">
            <button
              type="button"
              className={current === "all" ? btnPrimary : btn}
              aria-pressed={current === "all"}
              onClick={() => pickMode("all")}
              title="Находки по всем фото платья"
            >
              Все фото · {num(counts.all)} {plural(counts.all, "модель", "модели", "моделей")}
            </button>
            {dress.photos.map((p) => {
              const n = counts.byPhoto.get(p.id) ?? 0;
              const on = current === p.id;
              return (
                <button
                  key={p.id}
                  type="button"
                  disabled={n === 0}
                  aria-pressed={on}
                  onClick={() => pickMode(p.id)}
                  title={n === 0 ? "По этому фото ничего не нашлось" : "Только находки по этому фото"}
                  className={`inline-flex items-center gap-1.5 rounded-[var(--radius-ui)] border p-0.5 pr-2 text-[12px] tnum ${
                    on ? "border-accent bg-accent-dim text-accent-text" : "border-border hover:border-border-strong"
                  } disabled:cursor-not-allowed disabled:opacity-50`}
                >
                  <PhotoMini photo={p} size={40} />
                  {num(n)}
                </button>
              );
            })}
          </div>

          <div className="text-[12.5px] text-fg-muted" data-annot="итог поиска">
            Найдено <span className="tnum text-fg">{num(cards.length)}</span>{" "}
            {plural(cards.length, "модель", "модели", "моделей")}
            {found.data.missing > 0 ? (
              <span className="text-fg-faint">
                {" "}
                · {num(found.data.missing)} фото библиотек ещё без вектора
              </span>
            ) : null}
          </div>

          {cards.length === 0 ? (
            <Empty>Похожих референсов не нашлось.</Empty>
          ) : (
            <div className="grid grid-cols-[repeat(auto-fill,minmax(160px,1fr))] gap-3" data-annot="карточки моделей">
              {cards.slice(0, limit).map((card) => (
                <RefCardTile
                  key={card.key}
                  card={card}
                  photo={current === "all" && card.photoId !== null ? photos.get(card.photoId) : undefined}
                  on={selected.has(card.cover.id)}
                  onToggle={() => toggle(card.cover.id)}
                />
              ))}
            </div>
          )}
          {cards.length > limit ? (
            <div className="flex items-center justify-center gap-2 text-[12px] text-fg-faint tnum">
              показано {num(limit)} из {num(cards.length)}
              <button type="button" className={btn} onClick={() => setLimit(limit + PAGE)}>
                Показать ещё
              </button>
            </div>
          ) : null}

          {chosen.length ? (
            <div
              className="flex flex-wrap items-center gap-2 rounded-[var(--radius-ui)] border border-border bg-bg-subtle px-3 py-2 text-[12.5px]"
              data-annot="выбранные референсы"
            >
              <span className="tnum text-accent-text">Выбрано {num(chosen.length)}</span>
              {chosen.length > MAX_PAIRS ? (
                <span
                  className={`${btn} cursor-not-allowed opacity-50`}
                  aria-disabled="true"
                  title={`не больше ${MAX_PAIRS} за раз`}
                >
                  <Shirt size={13} />
                  Генерировать {num(chosen.length)}
                </span>
              ) : (
                <Link
                  className={btn}
                  to={fittingPath({ refs: chosen.map((c) => c.cover.id) }, dress.id)}
                  title="Обложки выделенных карточек — референсами на страницу «Генерация» с этим платьем"
                >
                  <Shirt size={13} />
                  Генерировать {num(chosen.length)}
                </Link>
              )}
              <button type="button" className={btn} onClick={() => setSelected(EMPTY)}>
                <X size={12} />
                Снять
              </button>
            </div>
          ) : null}
        </div>
      )}
    </Panel>
  );
}

/** Плитка карточки модели: клик — в новой вкладке, галочка — соседняя кнопка (не внутри ссылки). */
function RefCardTile({
  card,
  photo,
  on,
  onToggle,
}: {
  card: RefCard;
  /** Фото платья, давшее обложку (режим «Все фото»); нет — без миниатюры. */
  photo: Photo | undefined;
  on: boolean;
  onToggle: () => void;
}) {
  const { cover } = card;
  const ratio = cover.asset_width && cover.asset_height ? `${cover.asset_width} / ${cover.asset_height}` : "3 / 4";
  const name = card.model?.name ?? cover.note ?? `#${cover.id}`;
  return (
    <figure className="grid min-w-0 content-start gap-1" data-annot="карточка модели">
      <div className="group relative">
        <a
          href={cardHref(card)}
          target="_blank"
          rel="noreferrer"
          className={`relative block max-h-[360px] w-full overflow-hidden rounded-[var(--radius-card)] border border-border bg-bg-subtle focus-visible:outline-2 focus-visible:outline-accent ${
            on ? "outline-2 outline-offset-[-2px] outline-accent" : "hover:border-border-strong"
          }`}
          style={{ aspectRatio: ratio }}
          title={card.model ? "Открыть папку модели в библиотеке" : "Открыть карточку референса"}
        >
          {cover.asset_path ? (
            <RetryImg src={thumbUrl(cover.asset_path)} alt={name} className="absolute inset-0 h-full w-full object-cover" />
          ) : (
            <span className="absolute inset-0 grid place-items-center text-fg-faint">
              <ImageOff size={18} />
            </span>
          )}
          <span
            className="absolute top-1.5 left-1.5 rounded-[4px] bg-black/60 px-1.5 text-[11px] leading-[18px] text-white tnum"
            title="Сходство лучшего фото модели с платьем"
          >
            {scoreLabel(card.score)}
            {cover.view === "back" ? " · сзади" : ""}
          </span>
          {photo ? (
            <span className="absolute bottom-1.5 left-1.5" title="Нашлось по этому фото платья">
              <PhotoMini photo={photo} size={28} />
            </span>
          ) : null}
          <span
            className="absolute right-1.5 bottom-1.5 rounded-[4px] bg-black/60 px-1.5 text-[11px] leading-[18px] text-white tnum"
            title="Найдено фото модели"
          >
            {num(card.count)}
          </span>
        </a>
        <button
          type="button"
          aria-pressed={on}
          aria-label={on ? "Снять выделение" : "Выделить"}
          title="Выделить — обложка уйдёт в «Генерировать»"
          onClick={onToggle}
          className={`absolute top-1.5 right-1.5 grid size-5 place-items-center rounded-[4px] border ${
            on ? "border-accent bg-accent text-bg" : "border-white/80 bg-black/45 text-white hover:bg-black/65"
          }`}
        >
          {on ? <Check size={13} strokeWidth={3} /> : null}
        </button>
      </div>
      <figcaption className="truncate px-0.5 text-[11.5px] text-fg-muted" title={`${cover.library_name} · ${name}`}>
        <span className="text-fg-faint">{cover.library_name}</span> · {name}
      </figcaption>
    </figure>
  );
}

function PhotoMini({ photo, size }: { photo: Photo; size: number }) {
  const src = photoThumb(photo);
  return (
    <span
      className="block shrink-0 overflow-hidden rounded-[4px] border border-white/70 bg-bg-subtle"
      style={{ width: size, height: size }}
    >
      {src ? <RetryImg src={src} alt="" className="h-full w-full object-cover" /> : null}
    </span>
  );
}
